// DevHub: search-result hygiene (dedupe / merge / cleanup) applied before rendering.
//
// Ranking now happens SERVER-SIDE in the hybrid engine (lexical ⊕ semantic fused by Reciprocal Rank
// Fusion), so the frontend no longer re-scores results — it just cleans them up. `rankResults` is kept
// as the call site's name but is now a thin wrapper over `cleanAndDedupe`, preserving the server's
// order. All the hygiene below (mkdocs artifact cleanup, one-hit-per-page dedupe preferring the page
// root, catalog-entity↔techdocs-page merge that never drops the entity for a bare section) stays — it
// is presentation cleanup, not ranking.

import type { SearchResult } from '@backstage/plugin-search-common';

// Result types emitted by our collators. TechDocs pages are indexed as 'techdocs';
// catalog entities as 'software-catalog'.
const DOCS_TYPE = 'techdocs';

/** A TechDocs result pointing at a heading within a page (its location carries a #fragment).
 *  Used by the dedupe hygiene to prefer a page root over one of its sections. */
function isSection(result: SearchResult): boolean {
  return result.type === DOCS_TYPE && (result.document.location || '').includes('#');
}

/**
 * Clean up a backend result set for rendering, preserving the server's (RRF) order.
 * `term` is accepted for signature compatibility with existing call sites but no longer used.
 */
export function rankResults(
  results: SearchResult[],
  _term?: string,
): SearchResult[] {
  return cleanAndDedupe(results);
}

// ── Result hygiene ─────────────────────────────────────────────────────────
// MkDocs' search_index.json carries two artifacts into our results:
//   1. the headerlink pilcrow "¶" ends up in section titles/snippets, and
//   2. page results are titled "<Heading> | <Site Name> docs" — a doubling that
//      reads as a duplicate (e.g. "Apple iOS App Store | Apple iOS App Store docs").
// It also emits ONE hit per heading, so a single doc page shows up many times.
// We strip the artifacts and collapse to one hit per doc PAGE (by location minus
// the #section anchor), keeping the highest-ranked section. Applied to both the
// document fields and the highlight fields so the rendered (highlighted) text is clean.
const PILCROW = /¶/g;
// Middle group is length-bounded (titles are short) so the trailing `\s*$` can't force
// quadratic backtracking against `[^|]*` on a pathological title (Sonar dos hotspot).
const DOC_TITLE_SUFFIX = /\s*\|\s*[^|]{0,200}\bdocs\b\s*$/i;

const cleanTitle = (s?: string): string =>
  (s || '').replace(PILCROW, '').replace(DOC_TITLE_SUFFIX, '').replace(/\s{2,}/g, ' ').trim();

// A trailing, cut-off word before a terminal ellipsis. Several source descriptions
// are truncated mid-word (e.g. "Some key features includ…", "user admini…") — the "…"
// lands inside a word, which reads as broken. We trim the short (≤ 8 char) dangling
// fragment back to the previous word boundary so it reads "Some key features…" instead.
// The ≤ 8 cap leaves genuinely-complete trailing words ("installation…") intact.
const TRUNCATED_WORD = /\s+[^\s…]{1,8}…\s*$/u;
const cleanBody = (s?: string): string =>
  (s || '')
    .replace(PILCROW, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
    .replace(TRUNCATED_WORD, '…');

// A catalog entity and its TechDocs pages are "two sides of the same coin" (the `software-catalog`
// /catalog/<ns>/<kind>/<name> result and the `techdocs` /docs/<ns>/<kind>/<name>/… results are the
// SAME entity). We no longer DROP either — SearchResultGroups clusters them into ONE finding headed by
// the entity name, showing the doc first and the catalog item underneath. So here we only annotate each
// doc with the entity `kind` (for its row's kind badge) and otherwise keep every result intact.
const CATALOG_TYPE = 'software-catalog';
const ENTITY_LOCATION = /\/(?:docs|catalog)\/([^/]+)\/([^/]+)\/([^/?#]+)/i;

// Catalog search documents carry `kind` at runtime; it isn't in the base type.
type DocWithKind = SearchResult['document'] & { kind?: string };

/** kind:namespace/name from a /docs/… or /catalog/… location, or null. */
export function entityRefFromLocation(location?: string): string | null {
  const m = (location || '').match(ENTITY_LOCATION);
  if (!m) return null;
  const [, ns, kind, name] = m;
  return `${kind.toLowerCase()}:${ns.toLowerCase()}/${name.toLowerCase()}`;
}

function mergeEntityDuplicates(results: SearchResult[]): SearchResult[] {
  const catalogByRef = new Map<string, SearchResult>();
  for (const r of results) {
    const ref = entityRefFromLocation(r.document.location);
    if (ref && r.type === CATALOG_TYPE) catalogByRef.set(ref, r);
  }
  // Keep every result; just carry the entity kind onto its docs (for the row badge). Grouping — not
  // dropping — is how the entity and its docs are presented as a single finding now.
  return results.map(r => {
    const ref = entityRefFromLocation(r.document.location);
    if (r.type === DOCS_TYPE && ref && catalogByRef.has(ref) && !(r.document as DocWithKind).kind) {
      const catKind = (catalogByRef.get(ref)!.document as DocWithKind).kind;
      return { ...r, document: { ...r.document, kind: catKind } as DocWithKind };
    }
    return r;
  });
}

// The dedupe identity for a result. The engine emits one hit per heading, and the
// same page can arrive under near-identical locations that a naive split('#')[0]
// treats as distinct — so two hits at the SAME page read as a duplicate. We normalise
// away the differences that don't change the page:
//   • #fragment  (…/managed-settings/#sandbox      → …/managed-settings/)
//   • ?query     (…/managed-settings/?highlight=x   → …/managed-settings/)
//   • trailing / (…/managed-settings vs …/managed-settings/)
//   • case
// Genuinely different pages keep distinct keys, so legitimately-different results
// (e.g. a "Key Features" section on 15 different docs) are preserved.
function dedupeKey(location?: string, title?: string): string {
  const loc = (location || '')
    .split('#')[0]
    .split('?')[0]
    .replace(/\/+$/, '')
    .toLowerCase()
    .trim();
  return loc || (title || '').toLowerCase().trim();
}

/**
 * Strip MkDocs artifacts (pilcrow, doubled "| … docs" title) from titles/snippets,
 * de-duplicate to one result per doc page (location without the #anchor), and merge
 * each catalog entity with its own TechDocs page into a single doc-emphasised result.
 * Preserves input order (so it composes after rankResults). Exported for direct use
 * where ranking isn't wanted.
 */
export function cleanAndDedupe(results: SearchResult[]): SearchResult[] {
  const keyIndex = new Map<string, number>();
  const out: SearchResult[] = [];
  const isPageRoot = (r: SearchResult) =>
    r.type === DOCS_TYPE && !(r.document.location || '').includes('#');

  for (const r of results) {
    const document = {
      ...r.document,
      title: cleanTitle(r.document.title),
      text: cleanBody(r.document.text),
    };
    let highlight = r.highlight;
    if (highlight?.fields) {
      highlight = {
        ...highlight,
        fields: {
          ...highlight.fields,
          ...(highlight.fields.title !== undefined ? { title: cleanTitle(highlight.fields.title) } : {}),
          ...(highlight.fields.text !== undefined ? { text: cleanBody(highlight.fields.text) } : {}),
        },
      };
    }
    const entry = { ...r, document, highlight };

    const key = dedupeKey(r.document.location, r.document.title);
    if (!key) {
      out.push(entry);
      continue;
    }
    const existingIdx = keyIndex.get(key);
    if (existingIdx === undefined) {
      keyIndex.set(key, out.length);
      out.push(entry);
      continue;
    }
    // A page's sections collapse to one result. Prefer the PAGE ROOT as the survivor over a section
    // (so we show "Apple App Store", not "Apple App Store › Managed settings") — replacing in place
    // keeps the better-ranked position. Otherwise keep the first (highest-ranked) hit.
    // NOT dead code: the hybrid engine's `mergeVariants` (backend rrf.ts) already does this during
    // fusion, but it no-ops off Postgres — on that path this is the only thing collapsing a page's
    // sections, so it stays.
    if (isPageRoot(entry) && isSection(out[existingIdx])) {
      out[existingIdx] = entry;
    }
  }
  return mergeEntityDuplicates(out);
}
